import { z } from 'zod';

export const TopStatsResponseSchema = z.object({
    success: z.boolean(),
    message: z.string().optional(),
    code: z.number().optional(),
    data: z.object({
        total_revenue: z.object({
            value: z.number(),
            formatted: z.string(),
            percentage_change: z.number().optional(),
            trend: z.enum(["up", "down", "neutral"]).optional()
        }),
        total_bookings: z.object({
            value: z.number(),
            formatted: z.string(),
            percentage_change: z.number().optional(),
            trend: z.enum(["up", "down", "neutral"]).optional()
        }),
        available_rooms: z.object({
            value: z.number(),
            formatted: z.string(),
            total: z.number().optional(),
            formatted_ratio: z.string().optional(),
            percentage_change: z.number().optional(),
            trend: z.enum(["up", "down", "neutral"]).optional()
        }),
        revpar: z.object({
            value: z.number(),
            formatted: z.string(),
            subtitle: z.string().optional()
        }),
        arr: z.object({
            value: z.number(),
            formatted: z.string(),
            percentage_change: z.number().optional(),
            trend: z.enum(["up", "down", "neutral"]).optional()
        }),
        mtd_occupancy: z.object({
            value: z.number(),
            formatted: z.string()
        })
    })
});

export type TopStatsResponse = z.infer<typeof TopStatsResponseSchema>;

export const MonthlyPerformanceResponseSchema = z.object({
    success: z.boolean(),
    message: z.string().optional(),
    code: z.number().optional(),
    data: z.array(z.object({
        month: z.string(),
        month_label: z.string(),
        revenue: z.number(),
        arr: z.number(),
        occupancy: z.number().optional()
    }))
});

export type MonthlyPerformanceResponse = z.infer<typeof MonthlyPerformanceResponseSchema>;

export const RecentBookingsResponseSchema = z.object({
    success: z.boolean(),
    message: z.string().optional(),
    code: z.number().optional(),
    data: z.object({
        recent_bookings: z.array(z.object({
            id: z.string(),
            guest_name: z.string(),
            guest_initials: z.string(),
            room_type: z.string(),
            nights: z.number(),
            status: z.string(),
            created_at: z.string(),
            total_price: z.number(),
            total_price_formatted: z.string(),
            check_in: z.string(),
            check_out: z.string(),
            date_range_formatted: z.string(),
            voucher_number: z.string().nullable().optional(),
            invoice_number: z.string().nullable().optional()
        })),
        summary: z.object({
            avg_length_of_stay_nights: z.number(),
            avg_lead_time_days: z.number()
        })
    })
});

export type RecentBookingsResponse = z.infer<typeof RecentBookingsResponseSchema>;

export const BookingSourcesResponseSchema = z.object({
    success: z.boolean(),
    message: z.string().optional(),
    code: z.number().optional(),
    data: z.object({
        total_bookings: z.number(),
        sources: z.array(z.object({
            name: z.string(),
            count: z.number(),
            percentage: z.number(),
            color_hex: z.string().optional()
        }))
    })
});

export type BookingSourcesResponse = z.infer<typeof BookingSourcesResponseSchema>;

export const HousekeepingStatusResponseSchema = z.object({
    success: z.boolean(),
    message: z.string().optional(),
    code: z.number().optional(),
    data: z.object({
        clean: z.number(),
        dirty: z.number(),
        maintenance: z.number()
    })
});

export type HousekeepingStatusResponse = z.infer<typeof HousekeepingStatusResponseSchema>;

export const DashboardActivitiesResponseSchema = z.object({
    success: z.boolean(),
    message: z.string().optional(),
    code: z.number().optional(),
    data: z.object({
        summary_counts: z.object({
            today_check_in: z.number(),
            in_house: z.number(),
            today_check_out: z.number()
        }),
        list: z.array(z.object({
            booking_id: z.string(),
            invoice_number: z.string().nullable().optional(),
            guest_name: z.string(),
            rooms_info: z.string(),
            check_in: z.string(),
            check_out: z.string(),
            status: z.string()
        }))
    })
});

export type DashboardActivitiesResponse = z.infer<typeof DashboardActivitiesResponseSchema>;

export const IncompleteBookingsResponseSchema = z.object({
    success: z.boolean(),
    message: z.string().optional(),
    code: z.number().optional(),
    data: z.object({
        summary_counts: z.object({
            missing_room_type: z.number(),
            missing_rate_plan: z.number(),
            missing_room: z.number(),
            total: z.number(),
        }),
        list: z.object({
            items: z.array(z.object({
                booking_id: z.string(),
                invoice_number: z.string().nullable().optional(),
                guest_name: z.string(),
                room_type_name: z.string(),
                check_in: z.string(),
                check_out: z.string(),
                room_type_id: z.string().nullable(),
                rate_plan_id: z.string().nullable(),
                room_id: z.string().nullable(),
                source: z.string().nullable(),
            })),
            meta: z.object({
                current_page: z.number(),
                per_page: z.number(),
                total: z.number(),
                last_page: z.number(),
            }),
        }),
    })
});

export type IncompleteBookingsResponse = z.infer<typeof IncompleteBookingsResponseSchema>;
export type IncompleteBookingItem = IncompleteBookingsResponse['data']['list']['items'][number];
